import React, { useState, useEffect } from "react";
import {
  Box, Button, Grid, TextField, Typography, CircularProgress
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import ModalForms from "../../../Shared/Modals/index.js"; 
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SearchableDropdown from "../../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx";
import { HomeActions } from "../../../../Services/_actions/home.action.js";
import { ConstantActions } from "../../../../Services/_actions/constant.actions";
import { useSnackBar } from "../../../FlowBuilder/Common/SnackBar/index.tsx";
import ConfirmBox from "../../../FlowBuilder/Common/ConfirmBox/index.tsx";

const RedStar = () => (<span style={{ color: "red", marginLeft: 2, fontWeight: "bold" }}>*</span>);

// ─── Default values used in Quick Create ────────────────────────────────────
const QUICK_CREATE_DEFAULTS = {
  campaignType: "Offline",
  medium: "",
  description: "",
  locationCity: "",
  pincode: "",
  radiusKm: "",
  state: "",
  // Space types default to the first option "RS" (Societies)
  spaceCodes: ["RS"],
};

const CreateProposal = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const organisationName = searchParams.get("orgName") || "";
  const { showSnackBar } = useSnackBar();
  const location = useLocation();
  const passedState = location.state || {};
  const accountIdFromState = passedState.selectedAccount;
  const organisationIdFromState = passedState.organisationId;

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);

  const {
    loadInitialSupplierData, getSupplierAgencies, getAreasByLocation,
    getSubAreasByArea, createInitialProposal
  } = HomeActions();
  const constantActions = ConstantActions();

  // ── Shared state ────────────────────────────────────────────────────────────
  const [brands, setBrands] = useState([]);
  const [supplierAgencies, setSupplierAgencies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [subAreas, setSubAreas] = useState([]);
  const [selectedSpaceCodes, setSelectedSpaceCodes] = useState([]);

  const [formData, setFormData] = useState({
    campaignName: "", tentativeBudget: "", costOfLeads: "",
    principalVendor: organisationIdFromState || "",
    accountId: accountIdFromState || "",
    brand: "", state: "", city: "", area: "", subSubArea: "",
    locationCity: "", pincode: "", radiusKm: "",
    campaignType: "Offline", medium: "", description: ""
  });
  const [errors, setErrors] = useState({});

  // ── Quick Create modal state ─────────────────────────────────────────────────
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickLoading, setQuickLoading] = useState(false);
  const [quickForm, setQuickForm] = useState({
    campaignName: "",
    tentativeBudget: "",
    costOfLeads: "",
  });
  const [quickErrors, setQuickErrors] = useState({});

  const SPACE_TYPES = [
    { label: "Societies", code: "RS" }, { label: "Corporate Parks", code: "CP" },
    { label: "Bus Shelter", code: "BS" }, { label: "Gym", code: "GY" },
    { label: "Saloon", code: "SA" }, { label: "Retail Shop", code: "RE" }
  ];

  const BRAND_SELECTION_OPTIONS = [
    { label: "Single Brand", value: "single_brand" },
    { label: "Multi Brand", value: "multi_brand" }
  ];

  // ── Data fetch on mount ─────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      const initialData = (await loadInitialSupplierData()).data || {};
      const agenciesData = (await getSupplierAgencies()).data || [];
      setBrands((initialData.brands || []).map(b => ({ label: b.name, value: b.id })));
      setCities((initialData.cities || []).map(c => ({ label: c.city_name, value: c.id })));
      setSupplierAgencies(agenciesData.map(a => ({ label: a.name, value: a.organisation_id })));
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.principalVendor) {
      constantActions.getAccounts({ organisation_id: formData.principalVendor }).then(res => {
        const data = res.data?.list || res.data || [];
        setAccounts(data.map(acc => ({ label: acc.name, value: acc.account_id })));
      });
    }
  }, [formData.principalVendor]);

  // ── Full-form validation ────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!formData.campaignName.trim()) errs.campaignName = "Business Service Name is required";
    if (!formData.tentativeBudget.trim()) errs.tentativeBudget = "Tentative Budget is required";
    if (!formData.costOfLeads.trim()) errs.costOfLeads = "Cost Of Leads is required";
    if (!formData.principalVendor) errs.principalVendor = "Principal Vendor is required";
    if (!formData.accountId) errs.accountId = "Account is required";
    if (selectedSpaceCodes.length === 0) errs.spaceTypes = "Select at least one space type";
    if (!formData.area) errs.area = "Area is required";
    if (!formData.city) errs.city = "City is required";
    if (!formData.subSubArea) errs.subSubArea = "SubArea is required";
    return errs;
  };

  // ── Quick-form validation ───────────────────────────────────────────────────
  const validateQuick = () => {
    const errs = {};
    if (!quickForm.campaignName.trim()) errs.campaignName = "Business Service Name is required";
    if (!quickForm.tentativeBudget.toString().trim()) errs.tentativeBudget = "Tentative Budget is required";
    if (!quickForm.costOfLeads.toString().trim()) errs.costOfLeads = "Cost Of Leads is required";
    return errs;
  };

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));

    if (field === "city" && value) {
      getAreasByLocation(value).then(res => {
        const areaData = res.data || [];
        setAreas(areaData.map(a => ({ label: a.label, value: a.id })));
        setFormData(prev => ({ ...prev, area: "", subSubArea: "" }));
      });
    }
    if (field === "area" && value) {
      getSubAreasByArea(value).then(res => {
        const subData = res.data || [];
        setSubAreas(subData.map(sa => ({ label: sa.subarea_name, value: sa.id, subarea_code: sa.subarea_code })));
        setFormData(prev => ({ ...prev, subSubArea: "" }));
      });
    }
  };

  // Build the API payload (same structure regardless of full/quick form)
  const buildPayload = ({
    name, tentative_cost, cost_of_lead,
    principal_vendor, account_id, organisation_id,
    brand = "", description = "",
    address = "", pincode = "", radius = 0,
    subarea = "", area = "", city = "",
    codes = QUICK_CREATE_DEFAULTS.spaceCodes,
  }) => ({
    centers: [{
      center: {
        center_name: name,
        address,
        latitude: "",
        longitude: "",
        radius,
        subarea,
        area,
        city,
        pincode,
        codes,
      }
    }],
    vendorData: [1, 2, 3],
    brand,
    name,
    tentative_cost,
    cost_of_lead,
    principal_vendor,
    account_id,
    organisation_id,
    description,
  });

  // Full form submit
  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showSnackBar("Please fill all required fields", "error");
      return;
    }

    const selectedSubArea = subAreas.find(sa => sa.value === formData.subSubArea);
    const payload = buildPayload({
      name: formData.campaignName,
      tentative_cost: Number(formData.tentativeBudget),
      cost_of_lead: Number(formData.costOfLeads),
      principal_vendor: formData.principalVendor,
      account_id: formData.accountId,
      organisation_id: formData.principalVendor,
      brand: formData.brand,
      description: formData.description,
      address: formData.locationCity,
      pincode: formData.pincode,
      radius: Number(formData.radiusKm),
      subarea: selectedSubArea?.label || "",
      area: areas.find(a => a.value === formData.area)?.label || "",
      city: cities.find(c => c.value === formData.city)?.label || "",
      codes: selectedSpaceCodes,
    });

    try {
      const res = await createInitialProposal(formData.accountId, payload);
      setApiResponse(res?.data);
      showSnackBar("Business Service created successfully", "success");
      setIsConfirmDialogOpen(true);
    } catch (error) {
      console.error("Error submitting proposal:", error);
      showSnackBar("Failed to create Business Service", "error");
    }
  };

  // Quick Create submit — identical payload shape as manual form;
  // only principal_vendor & account_id come from session, rest are fixed defaults
  const handleQuickSubmit = async () => {
    const errs = validateQuick();
    if (Object.keys(errs).length > 0) {
      setQuickErrors(errs);
      return;
    }

    const principal_vendor = organisationIdFromState || "";
    const account_id       = accountIdFromState      || "";

    // Exact same object structure as manual form — only principal_vendor & account_id from session
    const payload = {
      centers: [
        {
          center: {
            center_name: quickForm.campaignName,  // user input
            address:     "Delhi",
            latitude:    "",
            longitude:   "",
            radius:      2,
            subarea:     "Basant Lok",
            area:        "Vasant Vihar",
            city:        "Delhi NCR",
            pincode:     "201309",
            codes:       ["RS"],
          }
        }
      ],
      vendorData:      [1, 2, 3],
      brand:           "single_brand",
      name:            quickForm.campaignName,          // user input
      tentative_cost:  Number(quickForm.tentativeBudget), // user input
      cost_of_lead:    Number(quickForm.costOfLeads),     // user input
      principal_vendor,                 // from session
      account_id,                       // from session
      organisation_id: principal_vendor, // same as principal_vendor
      description:     "",
    };

    setQuickLoading(true);
    try {
      const res = await createInitialProposal(account_id, payload);
      setApiResponse(res?.data);
      showSnackBar("Business Service created successfully", "success");
      setQuickOpen(false);
      setIsConfirmDialogOpen(true);
    } catch (error) {
      console.error("Quick Create error:", error);
      showSnackBar("Failed to create Business Service", "error");
    } finally {
      setQuickLoading(false);
    }
  };

  const handleConfirmation = async (isConfirmed: boolean) => {
    setIsConfirmDialogOpen(false);
    if (isConfirmed) {
      navigate(`/r/records/${apiResponse?.data}/createForm`);
    } else {
      navigate("/r/management/manageCampaign");
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        padding: "20px",
        maxWidth: "900px",
        margin: "auto",
        backgroundColor: "#f9f9f9",
        borderRadius: "8px",
        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
      }}
    >
      {/* ── Header row: title + Quick Create button ── */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: "bold" }}>
          Create Business Service
        </Typography>

        <Button
          variant="contained"
          startIcon={<BoltIcon />}
          onClick={() => {
            setQuickForm({ campaignName: "", tentativeBudget: "", costOfLeads: "" });
            setQuickErrors({});
            setQuickOpen(true);
          }}
          sx={{
            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
            color: "#fff",
            fontWeight: 600,
            borderRadius: "8px",
            textTransform: "none",
            boxShadow: "0 2px 8px rgba(99,102,241,0.35)",
            "&:hover": {
              background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
              boxShadow: "0 4px 14px rgba(99,102,241,0.45)",
            },
          }}
        >
          Quick Create
        </Button>
      </Box>

      {/* ── Full form ── */}
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} sm={6}>
          {/* Label changed: "Business Service Name" — payload key stays campaignName */}
          <TextField
            fullWidth size="small"
            label={<><span>Business Service Name</span><RedStar /></>}
            value={formData.campaignName}
            onChange={(e) => handleChange("campaignName", e.target.value)}
            error={!!errors.campaignName} helperText={errors.campaignName}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField fullWidth size="small" label={<><span>Tentative Budget</span><RedStar /></>} type="number"
            value={formData.tentativeBudget} onChange={(e) => handleChange("tentativeBudget", e.target.value)}
            error={!!errors.tentativeBudget} helperText={errors.tentativeBudget} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField fullWidth size="small" label={<><span>Cost of Leads</span><RedStar /></>} type="number"
            value={formData.costOfLeads} onChange={(e) => handleChange("costOfLeads", e.target.value)}
            error={!!errors.costOfLeads} helperText={errors.costOfLeads} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SearchableDropdown label={<><span>Principal Vendor</span><RedStar /></>} options={supplierAgencies}
            value={supplierAgencies.find(v => v.value === formData.principalVendor) || null}
            onChange={(v) => handleChange("principalVendor", v?.value)}
            error={!!errors.principalVendor} errorMessage={errors.principalVendor} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SearchableDropdown label={<><span>Account</span><RedStar /></>} options={accounts}
            value={accounts.find(v => v.value === formData.accountId) || null}
            onChange={(v) => handleChange("accountId", v?.value)}
            error={!!errors.accountId} errorMessage={errors.accountId} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SearchableDropdown
            label="Brand"
            options={[...brands, ...BRAND_SELECTION_OPTIONS.map(opt => ({ label: opt.label, value: opt.value }))]}
            value={[...brands, ...BRAND_SELECTION_OPTIONS].find(v => v.value === formData.brand) || null}
            onChange={(v) => handleChange("brand", v?.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <SearchableDropdown label={<><span>City</span><RedStar /></>} options={cities}
            value={cities.find(c => c.value === formData.city)}
            onChange={(v) => handleChange("city", v?.value)}
            error={!!errors.city} errorMessage={errors.city} />
        </Grid>
        <Grid item xs={6}>
          <SearchableDropdown label={<><span>Area</span><RedStar /></>} options={areas}
            value={areas.find(a => a.value === formData.area)}
            onChange={(v) => handleChange("area", v?.value)}
            error={!!errors.area} errorMessage={errors.area} />
        </Grid>
        <Grid item xs={6}>
          <SearchableDropdown label={<><span>Sub Area</span><RedStar /></>} options={subAreas}
            value={subAreas.find(sa => sa.value === formData.subSubArea)}
            onChange={(v) => handleChange("subSubArea", v?.value)}
            error={!!errors.subSubArea} errorMessage={errors.subSubArea} />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Location Address"
            value={formData.locationCity} onChange={(e) => handleChange("locationCity", e.target.value)} />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Pincode"
            value={formData.pincode} onChange={(e) => handleChange("pincode", e.target.value)} />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Radius (Km)" type="number"
            value={formData.radiusKm} onChange={(e) => handleChange("radiusKm", e.target.value)} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth size="small" label="Description" multiline rows={4}
            value={formData.description} onChange={(e) => handleChange("description", e.target.value)}
            placeholder="Enter campaign description" />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2">Select Space Types<RedStar /></Typography>
          <Grid container spacing={1}>
            {SPACE_TYPES.map(space => (
              <Grid item key={space.code}>
                <label>
                  <input type="checkbox" checked={selectedSpaceCodes.includes(space.code)}
                    onChange={(e) => {
                      const updated = e.target.checked
                        ? [...selectedSpaceCodes, space.code]
                        : selectedSpaceCodes.filter(c => c !== space.code);
                      setSelectedSpaceCodes(updated);
                    }} /> {space.label}
                </label>
              </Grid>
            ))}
          </Grid>
          {errors.spaceTypes && <Typography color="error" fontSize={12}>{errors.spaceTypes}</Typography>}
        </Grid>
      </Grid>

      <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained" color="primary" onClick={handleSubmit}>Save</Button>
      </Box>

      {/* ── Quick Create Modal using ModalForms ── */}
      <ModalForms
        show={quickOpen}
        handleClose={() => !quickLoading && setQuickOpen(false)}
        modalHeader={
          <Box display="flex" alignItems="center" gap={1}>
            <BoltIcon fontSize="small" sx={{ color: "#6366f1" }} />
            <span>Quick Create Business Service</span>
          </Box>
        }
        size="sm"
        modalBody={
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2.5}>
              Fill in just the essentials — all other values will be set automatically
              so you can create a Business Service in seconds.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={<><span>Business Service Name</span><RedStar /></>}
                  value={quickForm.campaignName}
                  onChange={(e) => {
                    setQuickForm(p => ({ ...p, campaignName: e.target.value }));
                    if (quickErrors.campaignName) setQuickErrors(p => ({ ...p, campaignName: undefined }));
                  }}
                  error={!!quickErrors.campaignName}
                  helperText={quickErrors.campaignName}
                  autoFocus
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={<><span>Tentative Budget</span><RedStar /></>}
                  type="number"
                  value={quickForm.tentativeBudget}
                  onChange={(e) => {
                    setQuickForm(p => ({ ...p, tentativeBudget: e.target.value }));
                    if (quickErrors.tentativeBudget) setQuickErrors(p => ({ ...p, tentativeBudget: undefined }));
                  }}
                  error={!!quickErrors.tentativeBudget}
                  helperText={quickErrors.tentativeBudget}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={<><span>Cost of Leads</span><RedStar /></>}
                  type="number"
                  value={quickForm.costOfLeads}
                  onChange={(e) => {
                    setQuickForm(p => ({ ...p, costOfLeads: e.target.value }));
                    if (quickErrors.costOfLeads) setQuickErrors(p => ({ ...p, costOfLeads: undefined }));
                  }}
                  error={!!quickErrors.costOfLeads}
                  helperText={quickErrors.costOfLeads}
                />
              </Grid>
            </Grid>

            {/* Info note about auto-filled values */}
            <Box
              mt={2.5} p={1.5}
              sx={{ background: "#f0f0ff", borderRadius: "8px", border: "1px solid #c7d2fe" }}
            >
              <Typography variant="caption" color="#4338ca" fontWeight={500}>
                ⚡ Auto-filled defaults: Space Type → Societies (RS) · Vendor, Account &
                Organisation from current session · All location fields left blank.
              </Typography>
            </Box>
          </Box>
        }
        modalFooter={
          <Box display="flex" justifyContent="flex-end" gap={1.5}>
            <Button
              variant="outlined"
              onClick={() => setQuickOpen(false)}
              disabled={quickLoading}
              sx={{ textTransform: "none", borderRadius: "8px" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleQuickSubmit}
              disabled={quickLoading}
              startIcon={quickLoading ? <CircularProgress size={16} color="inherit" /> : <BoltIcon />}
              sx={{
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 600,
                "&:hover": { background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)" },
              }}
            >
              {quickLoading ? "Creating..." : "Create"}
            </Button>
          </Box>
        }
      />

      {/* ── Confirm navigation dialog ── */}
      <ConfirmBox
        open={isConfirmDialogOpen}
        onClose={handleConfirmation}
        options={{
          title: "Form Create",
          text: "Do you want to create a Form?",
          confirmButtonText: "Yes",
          cancelButtonText: "Cancel",
          confirmButtonColor: "primary",
          cancelButtonColor: "default",
        }}
      />
    </Box>
  );
};

export default CreateProposal;